import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { ShareButton } from "@/components/ShareButton";
import { RatesBar } from "@/components/RatesBar";
import { FolderSelect } from "@/components/FolderSelect";
import { computeSheet, colName, emptyCells, sheetToCSV, sheetToHTMLTable } from "@/lib/sheetEngine";
import { PAGE, BTN_PRIMARY, BTN_GHOST, ICON_BTN } from "@/lib/styles";
import { ArrowLeft, Save, Check, Plus, Minus, Table2, Loader2, Rows3, Columns3, FileDown, FileSpreadsheet, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";

const MAX_ROWS = 200;
const MAX_COLS = 26;

export default function SheetEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [cells, setCells] = useState(null);
  const [folderId, setFolderId] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [focus, setFocus] = useState(null); // "r:c"
  const inputsRef = useRef({});

  // --- Historial Deshacer/Rehacer ---
  const cellsRef = useRef(null);
  useEffect(() => { cellsRef.current = cells; }, [cells]);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const editSession = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const syncHist = useCallback(() => { setCanUndo(pastRef.current.length > 0); setCanRedo(futureRef.current.length > 0); }, []);
  const pushHist = useCallback(() => {
    if (!cellsRef.current) return;
    pastRef.current.push(cellsRef.current.map((r) => r.slice()));
    if (pastRef.current.length > 60) pastRef.current.shift();
    futureRef.current = [];
    syncHist();
  }, [syncHist]);
  const undo = useCallback(() => {
    if (!pastRef.current.length || !cellsRef.current) return;
    futureRef.current.push(cellsRef.current.map((r) => r.slice()));
    setCells(pastRef.current.pop()); setDirty(true); syncHist();
  }, [syncHist]);
  const redo = useCallback(() => {
    if (!futureRef.current.length || !cellsRef.current) return;
    pastRef.current.push(cellsRef.current.map((r) => r.slice()));
    setCells(futureRef.current.pop()); setDirty(true); syncHist();
  }, [syncHist]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  useEffect(() => {
    let ignored = false;
    api.get(`/notes/${id}`).then(({ data }) => {
      if (ignored) return;
      setTitle(data.title || "Hoja de cálculo");
      const c = data.sheet?.cells;
      setCells(Array.isArray(c) && c.length ? c : emptyCells());
      setFolderId(data.folder_id || "");
    }).catch((err) => {
      if (ignored) return;
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/");
    });
    return () => { ignored = true; };
  }, [id, navigate]);

  const save = useCallback(async () => {
    if (!cells) return;
    setSaving(true);
    try {
      await api.put(`/notes/${id}`, { title: title || "Hoja de cálculo", sheet: { cells } });
      setDirty(false);
      toast.success("Guardado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  }, [id, title, cells]);

  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(save, 1200);
    return () => clearTimeout(t);
  }, [dirty, cells, title, save]);

  const { display } = useMemo(() => (cells ? computeSheet(cells) : { display: [] }), [cells]);

  const nrows = cells?.length || 0;
  const ncols = cells?.[0]?.length || 0;

  const setCell = (r, c, v) => {
    if (!editSession.current) { pushHist(); editSession.current = true; }
    setCells((prev) => {
      const next = prev.map((row) => row.slice());
      next[r][c] = v;
      return next;
    });
    setDirty(true);
  };

  const addRow = () => { if (nrows >= MAX_ROWS) return; pushHist(); setCells((p) => [...p, Array.from({ length: ncols }, () => "")]); setDirty(true); };
  const addCol = () => { if (ncols >= MAX_COLS) return; pushHist(); setCells((p) => p.map((row) => [...row, ""])); setDirty(true); };
  const delRow = () => { if (nrows <= 1) return; pushHist(); setCells((p) => p.slice(0, -1)); setDirty(true); };
  const delCol = () => { if (ncols <= 1) return; pushHist(); setCells((p) => p.map((row) => row.slice(0, -1))); setDirty(true); };

  const focusCell = (r, c) => {
    const el = inputsRef.current[`${r}:${c}`];
    if (el) el.focus();
  };

  const exportCsv = () => {
    const csv = sheetToCSV(cells);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "hoja").replace(/[\\/:*?"<>|]+/g, "").trim() || "hoja"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV descargado");
  };

  const exportPdf = () => {
    const table = sheetToHTMLTable(cells);
    const safeTitle = (title || "Hoja de cálculo").replace(/</g, "&lt;");
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
      <style>
        @page { margin: 16mm; }
        body { font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color:#0f172a; }
        h1.doc-title { font-size:24px; margin:0 0 4px; }
        .doc-meta { color:#64748b; font-size:12px; margin:0 0 20px; border-bottom:1px solid #e2e8f0; padding-bottom:10px; }
        table { border-collapse:collapse; width:100%; font-size:13px; }
        th,td { border:1px solid #cbd5e1; padding:6px 10px; text-align:left; }
        thead th { background:#f1f5f9; color:#334155; font-weight:600; }
        th.rownum { background:#f8fafc; color:#94a3b8; text-align:center; font-weight:500; }
      </style></head><body>
      <h1 class="doc-title">${safeTitle}</h1>
      <p class="doc-meta">Notacora · ${new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}</p>
      ${table}
      </body></html>`);
    doc.close();
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1500);
    }, 400);
    toast.info('En el diálogo, elige "Guardar como PDF" para descargar.');
  };

  const onKeyDown = (e, r, c) => {
    if (e.key === "Enter") { e.preventDefault(); focusCell(Math.min(r + 1, nrows - 1), c); }
    else if (e.key === "ArrowDown") { e.preventDefault(); focusCell(Math.min(r + 1, nrows - 1), c); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusCell(Math.max(r - 1, 0), c); }
    else if (e.key === "Tab") { e.preventDefault(); focusCell(r, c + 1 < ncols ? c + 1 : c); }
  };

  if (!cells) {
    return (
      <div className={`${PAGE} font-body`}>
        <Navbar />
        <div className="py-24 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={26} /></div>
      </div>
    );
  }

  return (
    <div className={`${PAGE} font-body`}>
      <Navbar />
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2.5">
            <button onClick={() => navigate("/")} className={ICON_BTN} data-testid="back-button"><ArrowLeft size={18} /></button>
            <FolderSelect value={folderId} onChange={async (v) => { setFolderId(v); try { await api.put(`/notes/${id}`, { folder_id: v }); toast.success(v ? "Movida a la carpeta" : "Quitada de la carpeta"); } catch { /* noop */ } }} />
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <button onClick={undo} disabled={!canUndo} className={`${ICON_BTN} disabled:opacity-40`} title="Deshacer (Ctrl+Z)" data-testid="sheet-undo"><Undo2 size={18} /></button>
            <button onClick={redo} disabled={!canRedo} className={`${ICON_BTN} disabled:opacity-40`} title="Rehacer (Ctrl+Y)" data-testid="sheet-redo"><Redo2 size={18} /></button>
            <span className="font-mono2 text-[11px] text-slate-400 flex items-center gap-1">
              {dirty ? "Editando..." : <><Check size={12} /> Guardado</>}
            </span>
            <button onClick={exportCsv} className={ICON_BTN} title="Exportar a CSV" data-testid="sheet-export-csv"><FileSpreadsheet size={18} /></button>
            <button onClick={exportPdf} className={ICON_BTN} title="Exportar a PDF" data-testid="sheet-export-pdf"><FileDown size={18} /></button>
            <RatesBar />
            <ShareButton targetType="note" targetId={id} />
            <button onClick={save} className={BTN_PRIMARY} disabled={saving} data-testid="save-sheet">
              <Save size={15} /> {saving ? "..." : "Guardar"}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Table2 size={26} className="text-emerald-500 shrink-0" />
          <input
            value={title}
            onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
            className="flex-1 bg-transparent font-heading font-light text-2xl sm:text-3xl tracking-tight focus:outline-none border-b border-transparent focus:border-indigo-500 transition-colors"
            placeholder="Título de la hoja"
            data-testid="sheet-title-input"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={addRow} className={`${BTN_GHOST} text-xs py-1.5`} data-testid="sheet-add-row"><Plus size={13} /> <Rows3 size={13} /> Fila</button>
          <button onClick={delRow} className={`${BTN_GHOST} text-xs py-1.5`} data-testid="sheet-del-row"><Minus size={13} /> <Rows3 size={13} /> Fila</button>
          <button onClick={addCol} className={`${BTN_GHOST} text-xs py-1.5`} data-testid="sheet-add-col"><Plus size={13} /> <Columns3 size={13} /> Columna</button>
          <button onClick={delCol} className={`${BTN_GHOST} text-xs py-1.5`} data-testid="sheet-del-col"><Minus size={13} /> <Columns3 size={13} /> Columna</button>
          <span className="font-mono2 text-[11px] text-slate-400 ml-auto">Escribe <code className="text-emerald-500">=A1+B2</code>, <code className="text-emerald-500">=SUMA(A1:A5)</code>, <code className="text-emerald-500">=PROMEDIO(B1:B3)</code></span>
        </div>

        <div className="overflow-auto border border-slate-200 dark:border-slate-800 rounded-xl" data-testid="sheet-grid">
          <table className="border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-10 min-w-[40px]"></th>
                {Array.from({ length: ncols }, (_, c) => (
                  <th key={c} className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono2 text-[11px] text-slate-500 min-w-[96px] px-2 py-1.5">{colName(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cells.map((row, r) => (
                <tr key={r}>
                  <td className="sticky left-0 z-10 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono2 text-[11px] text-slate-500 text-center w-10 min-w-[40px]">{r + 1}</td>
                  {row.map((_, c) => {
                    const key = `${r}:${c}`;
                    const editing = focus === key;
                    const shown = editing ? cells[r][c] : (display[r]?.[c] ?? "");
                    const isErr = typeof shown === "string" && (shown === "#ERROR" || shown === "#CICLO");
                    return (
                      <td key={c} className="border border-slate-200 dark:border-slate-800 p-0">
                        <input
                          ref={(el) => { inputsRef.current[key] = el; }}
                          value={shown}
                          onFocus={() => { setFocus(key); editSession.current = false; }}
                          onBlur={() => setFocus(null)}
                          onChange={(e) => setCell(r, c, e.target.value)}
                          onKeyDown={(e) => onKeyDown(e, r, c)}
                          className={`w-full min-w-[96px] px-2 py-1.5 bg-transparent focus:bg-indigo-50 dark:focus:bg-indigo-500/10 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-indigo-500 font-mono2 text-[13px] ${isErr ? "text-rose-500" : "text-slate-800 dark:text-slate-200"}`}
                          data-testid={`sheet-cell-${colName(c)}${r + 1}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
